<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount, onMounted } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import {
  totalSlots,
  snapUnit,
  freeRange,
  formatRange,
  blockTouchesSprint,
  dependencyIssues,
  STATUSES,
  SLOTS_PER_SPRINT
} from '@/lib/schema';
import { hasDates, sprintStartDate, formatDay, todaySlot, currentSprint, rangeDates, formatDateRange, slotToDate } from '@/lib/dates';
import { groupRows, dropTarget } from '@/lib/groups';
import { readPref, writePref } from '@/lib/prefs';
import { phaseVars } from '@/lib/palette';
import { openProject, openSettings } from '@/composables/useUi';
import { prompt } from '@/composables/useDialogs';
import BlockPopover from './BlockPopover.vue';
import Icon from '@/components/ui/Icon.vue';

const props = defineProps({
  roadmap: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
  filters: { type: Object, default: () => ({}) }
});

const emit = defineEmits(['open-project']);

const store = useRoadmapsStore();

const total = computed(() => totalSlots(props.roadmap));
const unit = computed(() => snapUnit(props.roadmap));
const sprintCount = computed(() => props.roadmap.settings.sprintCount);
const compact = computed(() => sprintCount.value > 12);
const phaseById = computed(() => new Map(props.roadmap.phases.map((p) => [p.id, p])));
const memberById = computed(() => new Map(props.roadmap.members.map((m) => [m.id, m])));
const projectById = computed(() => new Map(props.roadmap.projects.map((p) => [p.id, p])));
const dated = computed(() => hasDates(props.roadmap));
const issues = computed(() => dependencyIssues(props.roadmap));
const statusLabel = (v) => STATUSES.find((s) => s.value === v)?.label || '';

const track = ref(null);
const body = ref(null);
const quickName = ref('');
const quickInput = ref(null);

const selected = ref([]);
const draft = ref(null);
const ghost = ref(null);
const hover = ref(null);
const rowDrag = ref(null);
const popover = ref(null);
const now = ref(new Date());
let gesture = null;
let clock = null;

/* ---------- swimlanes ---------- */

const collapsed = ref(new Set());

function loadCollapsed() {
  collapsed.value = new Set(readPref(`collapsed:${props.roadmap.id}`, []) || []);
}

function toggleGroup(group) {
  const next = new Set(collapsed.value);
  next.has(group) ? next.delete(group) : next.add(group);
  collapsed.value = next;
  writePref(`collapsed:${props.roadmap.id}`, next.size ? [...next] : null);
}

async function renameGroup(row) {
  if (props.readonly || !row.group) return;
  const name = await prompt({ title: 'Rename group', label: 'Group name', value: row.group, confirmLabel: 'Rename' });
  if (name === null || name.trim() === row.group) return;
  store.renameGroup(props.roadmap.id, row.group, name.trim());
}

const rows = computed(() => groupRows(props.roadmap.projects, collapsed.value));
const rowOf = computed(() => {
  const map = new Map();
  rows.value.forEach((r, i) => {
    if (r.type === 'project') map.set(r.project.id, i);
  });
  return map;
});
const gridRowOf = (projectId) => (rowOf.value.get(projectId) ?? -1) + 1;

function groupRuns(row) {
  const busy = new Array(total.value).fill(false);
  for (const p of row.projects) for (const b of p.blocks) for (let s = b.start; s < b.start + b.span && s < total.value; s++) busy[s] = true;
  const runs = [];
  let i = 0;
  while (i < busy.length) {
    if (!busy[i]) {
      i++;
      continue;
    }
    let j = i;
    while (j < busy.length && busy[j]) j++;
    runs.push({ start: i, span: j - i });
    i = j;
  }
  return runs;
}

watch(
  () => props.roadmap.id,
  () => {
    selected.value = [];
    popover.value = null;
    loadCollapsed();
  },
  { immediate: true }
);

const gridStyle = computed(() => ({ '--slots': total.value }));

/* ---------- dates, today and milestones ---------- */

const sprintLabels = computed(() =>
  Array.from({ length: sprintCount.value }, (_, i) => {
    const n = i + 1;
    return {
      n,
      label: compact.value ? `S${n}` : `Sprint ${n}`,
      date: dated.value ? formatDay(sprintStartDate(props.roadmap.settings, n)) : '',
      title: dated.value ? formatDateRange(rangeDates(props.roadmap.settings, i * 2, 2)) : ''
    };
  })
);

const today = computed(() => todaySlot(props.roadmap, total.value, now.value));
const thisSprint = computed(() => currentSprint(props.roadmap, now.value));
const pct = (slot) => `${(slot / total.value) * 100}%`;

const milestones = computed(() =>
  (props.roadmap.milestones || []).map((m) => {
    const when = m.date ? formatDay(m.date, { year: true }) : dated.value ? formatDay(slotToDate(props.roadmap.settings, m.slot), { year: true }) : '';
    // Flags near the right edge put their label on the left so it is not cut off.
    return { ...m, when, title: [m.name, when].filter(Boolean).join(', '), flip: m.slot / total.value > 0.72 };
  })
);

function openMilestones() {
  if (!props.readonly) openSettings(props.roadmap.id, 'milestones');
}

/* ---------- focus and filters ---------- */

function toggleSprint(n) {
  const i = selected.value.indexOf(n);
  if (i === -1) selected.value = [...selected.value, n].sort((a, b) => a - b);
  else selected.value = selected.value.filter((x) => x !== n);
}

const filtering = computed(() => Boolean(props.filters.query?.trim() || props.filters.memberId || props.filters.phaseId || props.filters.status));

function projectMatches(project) {
  const f = props.filters;
  if (f.memberId && !project.memberIds.includes(f.memberId)) return false;
  if (f.status && (project.status || 'none') !== f.status) return false;
  if (f.phaseId && !project.blocks.some((b) => (b.phaseId || 'none') === f.phaseId)) return false;
  const q = f.query?.trim().toLowerCase();
  if (q) {
    const people = project.memberIds.map((id) => memberById.value.get(id)?.name || '').join(' ');
    const hay = `${project.name} ${project.description} ${project.group} ${people}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

function isDimmed(block, project) {
  if (filtering.value && !projectMatches(project)) return true;
  if (props.filters.phaseId && (block.phaseId || 'none') !== props.filters.phaseId) return true;
  if (!selected.value.length) return false;
  return !selected.value.some((n) => blockTouchesSprint(block, n));
}

function isProjectDimmed(project) {
  if (filtering.value && !projectMatches(project)) return true;
  if (!selected.value.length) return false;
  return !project.blocks.some((b) => !isDimmed(b, project));
}

const matchCount = computed(() => props.roadmap.projects.filter(projectMatches).length);
defineExpose({ matchCount });

/* ---------- drawing helpers ---------- */

function position(block) {
  return draft.value?.blockId === block.id ? draft.value : block;
}

function phaseOf(block) {
  return block.phaseId ? phaseById.value.get(block.phaseId) || null : null;
}

function blockStyle(project, block) {
  const pos = position(block);
  const phase = phaseOf(block);
  return {
    gridRow: gridRowOf(project.id),
    gridColumn: `${pos.start + 2} / span ${pos.span}`,
    ...phaseVars(phase ? phase.color : null)
  };
}

function blockTitle(block) {
  const pos = position(block);
  const phase = phaseOf(block);
  const range = dated.value ? `, ${formatDateRange(rangeDates(props.roadmap.settings, pos.start, pos.span))}` : '';
  return `${phase ? phase.name : 'No phase'}, ${formatRange(pos.start, pos.span)}${range}`;
}

function initials(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

function membersOf(project) {
  return project.memberIds.map((id) => memberById.value.get(id)).filter(Boolean);
}

function issueText(project) {
  const list = issues.value.get(project.id);
  if (!list) return '';
  return list.map((i) => `Starts before ${i.project.name} finishes`).join('. ');
}

function dependencyText(project) {
  const names = (project.dependsOn || []).map((id) => projectById.value.get(id)?.name).filter(Boolean);
  return names.length ? `Depends on ${names.join(', ')}` : '';
}

/* ---------- pointer gestures ---------- */

function metrics() {
  const rect = track.value.getBoundingClientRect();
  return { left: rect.left, slotW: rect.width / total.value };
}

function slotAt(clientX) {
  const { left, slotW } = metrics();
  return Math.max(0, Math.min(total.value - 1, Math.floor((clientX - left) / slotW)));
}

function listen() {
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
}

function unlisten() {
  window.removeEventListener('pointermove', onMove);
  window.removeEventListener('pointerup', onUp);
  window.removeEventListener('pointercancel', onCancel);
  document.body.classList.remove('is-dragging', 'is-resizing');
}

function onBlockDown(e, project, block, kind) {
  if (props.readonly || e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  const range = freeRange(project, block.start, block.id, total.value) || { min: block.start, max: block.start + block.span };
  gesture = {
    kind,
    projectId: project.id,
    blockId: block.id,
    x0: e.clientX,
    start: block.start,
    span: block.span,
    min: range.min,
    max: range.max,
    slotW: metrics().slotW,
    moved: false
  };
  listen();
}

function onLaneDown(e, project) {
  if (props.readonly || e.button !== 0) return;
  e.preventDefault();
  const u = unit.value;
  const raw = slotAt(e.clientX);
  let anchor = Math.floor(raw / u) * u;
  let range = freeRange(project, anchor, null, total.value);
  if (!range) {
    anchor = raw;
    range = freeRange(project, anchor, null, total.value);
  }
  if (!range) return;
  popover.value = null;
  gesture = { kind: 'create', projectId: project.id, anchor, min: range.min, max: range.max, moved: false };
  ghost.value = { projectId: project.id, start: anchor, span: Math.min(u, range.max - anchor) };
  listen();
}

function onGripDown(e, displayIndex, project) {
  if (props.readonly || e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  const marks = [...body.value.querySelectorAll('[data-row-index]')]
    .map((el) => {
      const r = el.getBoundingClientRect();
      return { index: Number(el.dataset.rowIndex), mid: r.top + r.height / 2 };
    })
    .sort((a, b) => a.index - b.index);
  gesture = { kind: 'row', from: displayIndex, projectId: project.id, y0: e.clientY, marks, moved: false };
  rowDrag.value = { from: displayIndex, to: displayIndex };
  listen();
}

function onMove(e) {
  if (!gesture) return;
  const u = unit.value;

  if (gesture.kind === 'row') {
    if (!gesture.moved && Math.abs(e.clientY - gesture.y0) < 4) return;
    gesture.moved = true;
    document.body.classList.add('is-dragging');
    const to = gesture.marks.filter((m) => m.mid < e.clientY).length;
    rowDrag.value = { from: gesture.from, to };
    return;
  }

  if (gesture.kind === 'create') {
    const cur = Math.floor(slotAt(e.clientX) / u) * u;
    if (cur !== gesture.anchor) gesture.moved = true;
    const a = Math.max(Math.min(gesture.anchor, cur), gesture.min);
    const b = Math.min(Math.max(gesture.anchor, cur) + u, gesture.max);
    ghost.value = { projectId: gesture.projectId, start: a, span: Math.max(1, b - a) };
    return;
  }

  const dx = e.clientX - gesture.x0;
  if (!gesture.moved && Math.abs(dx) < 4) return;
  if (!gesture.moved) {
    gesture.moved = true;
    popover.value = null;
    document.body.classList.add(gesture.kind === 'move' ? 'is-dragging' : 'is-resizing');
  }

  const delta = Math.round(dx / gesture.slotW / u) * u;
  const { start, span, min, max, blockId } = gesture;
  const end = start + span;
  const minSpan = Math.min(u, span);

  if (gesture.kind === 'move') {
    const s = Math.max(min, Math.min(max - span, start + delta));
    draft.value = { blockId, start: s, span };
  } else if (gesture.kind === 'right') {
    const newEnd = Math.max(start + minSpan, Math.min(max, end + delta));
    draft.value = { blockId, start, span: newEnd - start };
  } else {
    const s = Math.max(min, Math.min(end - minSpan, start + delta));
    draft.value = { blockId, start: s, span: end - s };
  }
}

function onUp() {
  const g = gesture;
  gesture = null;
  unlisten();
  if (!g) return;

  if (g.kind === 'row') {
    const r = rowDrag.value;
    rowDrag.value = null;
    if (g.moved && r) {
      const target = dropTarget(rows.value, r.to);
      const project = projectById.value.get(g.projectId);
      const sameGroup = target.group === undefined || target.group === (project?.group || '');
      if (r.to === r.from || r.to === r.from + 1) {
        if (!sameGroup) store.placeProject(props.roadmap.id, g.projectId, target);
        return;
      }
      store.placeProject(props.roadmap.id, g.projectId, target);
    } else if (!g.moved) {
      openProject(props.roadmap.id, g.projectId);
    }
    return;
  }

  if (g.kind === 'create') {
    const gh = ghost.value;
    ghost.value = null;
    if (!gh) return;
    const block = store.addBlock(props.roadmap.id, g.projectId, {
      start: gh.start,
      span: gh.span,
      phaseId: store.defaultPhaseId(props.roadmap.id)
    });
    if (block) showPopover(g.projectId, block.id);
    return;
  }

  const d = draft.value;
  draft.value = null;
  if (g.moved) {
    if (d && (d.start !== g.start || d.span !== g.span)) {
      store.updateBlock(props.roadmap.id, g.projectId, g.blockId, { start: d.start, span: d.span });
    }
    return;
  }
  showPopover(g.projectId, g.blockId);
}

function onCancel() {
  gesture = null;
  draft.value = null;
  ghost.value = null;
  rowDrag.value = null;
  unlisten();
}

function showPopover(projectId, blockId) {
  nextTick(() => {
    const el = body.value?.querySelector(`[data-block="${blockId}"]`);
    if (!el) return;
    popover.value = { projectId, blockId, rect: el.getBoundingClientRect() };
  });
}

function onBlockClick(project) {
  if (props.readonly) emit('open-project', project.id);
}

function onBlockKey(e, project, block) {
  if (props.readonly) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      emit('open-project', project.id);
    }
    return;
  }
  const u = unit.value;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    showPopover(project.id, block.id);
    return;
  }
  if (e.key === 'Delete' || e.key === 'Backspace') {
    e.preventDefault();
    store.removeBlock(props.roadmap.id, project.id, block.id);
    return;
  }
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
  e.preventDefault();
  const dir = e.key === 'ArrowLeft' ? -1 : 1;
  const range = freeRange(project, block.start, block.id, total.value);
  if (!range) return;
  if (e.shiftKey) {
    const end = Math.max(block.start + Math.min(u, block.span), Math.min(range.max, block.start + block.span + dir * u));
    store.updateBlock(props.roadmap.id, project.id, block.id, { span: end - block.start });
  } else {
    const s = Math.max(range.min, Math.min(range.max - block.span, block.start + dir * u));
    store.updateBlock(props.roadmap.id, project.id, block.id, { start: s });
  }
  nextTick(() => body.value?.querySelector(`[data-block="${block.id}"]`)?.focus());
}

function onLaneHover(e, project) {
  if (gesture || props.readonly) return;
  const u = unit.value;
  const slot = Math.floor(slotAt(e.clientX) / u) * u;
  const range = freeRange(project, slot, null, total.value);
  hover.value = range ? { projectId: project.id, start: slot, span: Math.min(u, range.max - slot) } : null;
}

function openLabel(project) {
  if (props.readonly) emit('open-project', project.id);
  else openProject(props.roadmap.id, project.id);
}

function addQuickProject() {
  const name = quickName.value.trim();
  if (!name) return;
  store.addProject(props.roadmap.id, { name });
  quickName.value = '';
  nextTick(() => quickInput.value?.focus());
}

const indicatorRow = computed(() => (rowDrag.value && rowDrag.value.to !== rowDrag.value.from && rowDrag.value.to !== rowDrag.value.from + 1 ? rowDrag.value.to : null));

onMounted(() => {
  clock = setInterval(() => (now.value = new Date()), 60000);
});

onBeforeUnmount(() => {
  unlisten();
  clearInterval(clock);
});
</script>

<template>
  <div class="board" :class="{ 'is-half': roadmap.settings.halfSprints, 'is-readonly': readonly, 'is-dated': dated, 'is-compact': compact }" :style="gridStyle">
    <div class="board-head" :class="{ 'has-marks': milestones.length || today !== null }">
      <div class="corner">
        <span>Projects</span>
        <button v-if="selected.length" type="button" class="clear" @click="selected = []">Clear focus</button>
      </div>
      <div ref="track" class="head-track">
        <button
          v-for="s in sprintLabels"
          :key="s.n"
          type="button"
          class="sprint"
          :class="{ selected: selected.includes(s.n), current: thisSprint === s.n }"
          :aria-pressed="selected.includes(s.n)"
          :title="[s.title, selected.includes(s.n) ? 'Stop focusing this sprint' : 'Focus this sprint'].filter(Boolean).join('. ')"
          @click="toggleSprint(s.n)"
        >
          <span class="sprint-name">{{ s.label }}</span>
          <span v-if="s.date" class="sprint-date">{{ s.date }}</span>
        </button>
        <div v-if="milestones.length || today !== null" class="head-marks">
          <button
            v-for="m in milestones"
            :key="m.id"
            type="button"
            class="flag"
            :class="{ 'flag--flip': m.flip }"
            :style="{ left: pct(m.slot) }"
            :title="m.title"
            :aria-label="`Milestone: ${m.title}`"
            @click="openMilestones"
          >
            <i />
            <span>{{ m.name }}</span>
          </button>
          <span v-if="today !== null" class="today-tag" :style="{ left: pct(today) }">Today</span>
        </div>
      </div>
    </div>

    <div ref="body" class="board-body">
      <div v-if="roadmap.projects.length" class="columns" aria-hidden="true">
        <div
          v-for="n in sprintCount"
          :key="n"
          class="col"
          :class="{ selected: selected.includes(n), alt: n % 2 === 0, current: thisSprint === n }"
          :style="{ gridColumn: `${(n - 1) * SLOTS_PER_SPRINT + 2} / span ${SLOTS_PER_SPRINT}` }"
        />
      </div>

      <div v-if="milestones.length || today !== null" class="marks" aria-hidden="true">
        <span v-for="m in milestones" :key="m.id" class="mark mark--milestone" :style="{ left: pct(m.slot) }" />
        <span v-if="today !== null" class="mark mark--today" :style="{ left: pct(today) }" />
      </div>

      <template v-for="(row, i) in rows" :key="row.key">
        <template v-if="row.type === 'group'">
          <div class="group-row" :style="{ gridRow: i + 1 }" :data-row-index="i">
            <button type="button" class="group-toggle" :aria-expanded="!row.collapsed" @click="toggleGroup(row.group)" @dblclick="renameGroup(row)">
              <Icon :name="row.collapsed ? 'chevronRight' : 'chevronDown'" :size="14" />
              <span class="group-name">{{ row.name }}</span>
              <span class="group-count">{{ row.count }}</span>
            </button>
          </div>
          <template v-if="row.collapsed">
            <span
              v-for="run in groupRuns(row)"
              :key="`${row.key}-${run.start}`"
              class="group-run"
              :style="{ gridRow: i + 1, gridColumn: `${run.start + 2} / span ${run.span}` }"
            />
          </template>
        </template>

        <template v-else>
          <div
            class="label"
            :class="{ dim: isProjectDimmed(row.project), lifted: rowDrag && rowDrag.from === i }"
            :style="{ gridRow: i + 1 }"
            :data-row-index="i"
            role="button"
            tabindex="0"
            @click="openLabel(row.project)"
            @keydown.enter="openLabel(row.project)"
          >
            <span v-if="!readonly" class="grip" title="Drag to reorder" @pointerdown="onGripDown($event, i, row.project)" @click.stop>
              <Icon name="grip" :size="14" />
            </span>
            <span v-else class="grip-space" />
            <span class="label-name" :title="[row.project.name, dependencyText(row.project)].filter(Boolean).join('. ')">{{ row.project.name }}</span>
            <span v-if="issues.has(row.project.id)" class="warn" :title="issueText(row.project)" :aria-label="issueText(row.project)">
              <Icon name="alert" :size="13" />
            </span>
            <span v-if="row.project.links?.length" class="has-links" :title="`${row.project.links.length} link${row.project.links.length === 1 ? '' : 's'}`">
              <Icon name="link" :size="12" />
            </span>
            <span
              v-if="row.project.status && row.project.status !== 'committed'"
              class="status"
              :class="`status--${row.project.status}`"
              :title="statusLabel(row.project.status)"
              :aria-label="statusLabel(row.project.status)"
            />
            <span v-if="row.project.memberIds.length" class="avatars">
              <span v-for="m in membersOf(row.project).slice(0, 3)" :key="m.id" class="avatar" :title="m.name">{{ initials(m.name) }}</span>
              <span v-if="row.project.memberIds.length > 3" class="avatar avatar--more">+{{ row.project.memberIds.length - 3 }}</span>
            </span>
          </div>

          <div
            class="lane"
            :style="{ gridRow: i + 1 }"
            @pointerdown="onLaneDown($event, row.project)"
            @pointermove="onLaneHover($event, row.project)"
            @pointerleave="hover = null"
          />

          <div
            v-for="block in row.project.blocks"
            :key="block.id"
            :data-block="block.id"
            class="block"
            :class="[
              {
                dim: isDimmed(block, row.project),
                active: draft?.blockId === block.id || popover?.blockId === block.id,
                narrow: position(block).span <= 1
              },
              row.project.status ? `block--${row.project.status}` : ''
            ]"
            :style="blockStyle(row.project, block)"
            :title="blockTitle(block)"
            tabindex="0"
            @pointerdown="onBlockDown($event, row.project, block, 'move')"
            @click="onBlockClick(row.project)"
            @keydown="onBlockKey($event, row.project, block)"
          >
            <span v-if="!readonly" class="handle handle--left" @pointerdown="onBlockDown($event, row.project, block, 'left')" />
            <span class="block-text">{{ phaseOf(block)?.name || 'No phase' }}</span>
            <span v-if="!readonly" class="handle handle--right" @pointerdown="onBlockDown($event, row.project, block, 'right')" />
          </div>
        </template>
      </template>

      <div
        v-if="ghost"
        class="ghost"
        :style="{ gridRow: gridRowOf(ghost.projectId), gridColumn: `${ghost.start + 2} / span ${ghost.span}` }"
      />
      <div
        v-else-if="hover && !popover"
        class="hint"
        :style="{ gridRow: gridRowOf(hover.projectId), gridColumn: `${hover.start + 2} / span ${hover.span}` }"
      >
        <Icon name="plus" :size="14" />
      </div>

      <div v-if="indicatorRow !== null" class="row-indicator" :style="{ gridRow: indicatorRow + 1 }" />

      <form v-if="!readonly" class="quick-add" :style="{ gridRow: rows.length + 1 }" @submit.prevent="addQuickProject">
        <Icon name="plus" :size="14" />
        <input
          ref="quickInput"
          v-model="quickName"
          class="quick-input"
          type="text"
          placeholder="Add a project"
          aria-label="New project name"
        />
      </form>
      <p v-if="!roadmap.projects.length" class="empty-hint" :style="{ gridRow: 1 }">
        {{ readonly ? 'No projects on this roadmap yet.' : 'Name your first project on the left, then drag across its row to plan it.' }}
      </p>
    </div>

    <BlockPopover
      v-if="popover && !readonly"
      :roadmap="roadmap"
      :project-id="popover.projectId"
      :block-id="popover.blockId"
      :anchor="popover.rect"
      @close="popover = null"
    />
  </div>
</template>

<style scoped lang="scss">
.board {
  --grid: var(--label-w) repeat(var(--slots), minmax(0, 1fr));
  user-select: none;

  &.is-readonly {
    min-width: calc(var(--label-w) + var(--slots) * 30px);

    .lane {
      cursor: default;
    }

    .block {
      cursor: pointer;
    }
  }
}

.board-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: var(--label-w) 1fr;
  padding: 10px 0;
  background: var(--bg);

  &.has-marks {
    padding-bottom: 34px;
  }
}

.corner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px 0 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-3);
}

.clear {
  border: 0;
  background: none;
  padding: 2px 4px;
  font-size: 12px;
  color: var(--iris-strong);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.head-track {
  display: grid;
  grid-template-columns: repeat(var(--slots), minmax(0, 1fr));
}

.head-track {
  position: relative;
}

.sprint {
  grid-column: span 2;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 32px;
  padding: 0 4px;
  line-height: 1.15;
  overflow: hidden;

  .is-dated & {
    height: 40px;
  }

  &.current {
    border-color: color-mix(in srgb, var(--today) 55%, var(--line));
    color: var(--text);
  }
  margin: 0 2px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-2);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;

  .is-half &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 1px;
    height: 6px;
    background: var(--line-strong);
  }

  &:hover {
    border-color: var(--line-strong);
    color: var(--text);
  }

  &.selected {
    border-color: var(--iris);
    background: var(--iris-soft);
    color: var(--iris-strong);
  }
}

.sprint-name,
.sprint-date {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sprint-date {
  font-size: 10.5px;
  font-weight: 400;
  color: var(--text-3);
}

.head-marks {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 0;
  pointer-events: none;
}

.flag {
  position: absolute;
  top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 160px;
  padding: 1px 6px 1px 0;
  border: 0;
  background: none;
  color: var(--milestone);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  transform: translateX(-5px);
  pointer-events: auto;
  cursor: pointer;

  i {
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    background: var(--milestone);
    transform: rotate(45deg);
    border-radius: 2px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 4px;
    border-radius: 4px;
    background: var(--bg);
  }

  &:hover span {
    text-decoration: underline;
  }

  &--flip {
    flex-direction: row-reverse;
    padding: 1px 0 1px 6px;
    transform: translateX(calc(-100% + 5px));
  }

  .is-readonly & {
    cursor: default;

    &:hover span {
      text-decoration: none;
    }
  }
}

.today-tag {
  position: absolute;
  top: 6px;
  transform: translateX(-50%);
  padding: 0 5px;
  border-radius: 4px;
  background: var(--today);
  color: #fff;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 15px;
}

.marks {
  position: absolute;
  top: 0;
  bottom: 32px;
  left: var(--label-w);
  right: 0;
  z-index: 2;
  pointer-events: none;
}

.mark {
  position: absolute;
  top: -10px;
  bottom: 0;
  width: 0;

  &--milestone {
    border-left: 1.5px dashed var(--milestone);
    opacity: 0.8;
  }

  &--today {
    border-left: 2px solid var(--today);
  }
}

.group-row {
  grid-column: 1 / -1;
  display: flex;
  align-items: flex-end;
  padding: 0 0 4px 2px;
  border-bottom: 1px solid var(--line);
  z-index: 2;
}

.group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border: 0;
  border-radius: 6px;
  background: none;
  color: var(--text-2);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
    color: var(--text);
  }
}

.group-count {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--track-bg);
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0;
  text-align: center;
}

.group-run {
  z-index: 3;
  align-self: center;
  height: 8px;
  margin: 0 2px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--iris) 35%, var(--mix-base));
  pointer-events: none;
}

.board-body {
  position: relative;
  display: grid;
  grid-template-columns: var(--grid);
  grid-auto-rows: var(--row-h);
  row-gap: var(--row-gap);
  padding-bottom: 32px;
}

.columns {
  position: absolute;
  inset: 0 0 32px;
  display: grid;
  grid-template-columns: var(--grid);
  grid-template-rows: 100%;
  pointer-events: none;
}

.col {
  grid-row: 1;
  height: 100%;
  margin: 0 2px;
  border-radius: 8px;
  position: relative;

  &.alt {
    background: var(--col-alt);
  }

  .is-half &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    border-left: 1px dashed var(--col-dash);
  }

  &.selected {
    background: var(--iris-tint);
    box-shadow: inset 0 0 0 1px rgba(107, 108, 247, 0.35);
  }

  &.current:not(.selected) {
    background: color-mix(in srgb, var(--today) 6%, transparent);
  }
}

.label {
  grid-column: 1;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 10px;
  padding: 0 10px 0 2px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  cursor: pointer;
  transition: border-color 0.15s, opacity 0.2s, box-shadow 0.15s;

  &:hover {
    border-color: var(--line-strong);
    box-shadow: var(--shadow-1);

    .grip {
      opacity: 1;
    }
  }

  &.dim {
    opacity: 0.35;
  }

  &.lifted {
    opacity: 0.5;
  }
}

.grip {
  display: grid;
  place-items: center;
  width: 20px;
  height: 28px;
  color: var(--text-3);
  cursor: grab;
  opacity: 0;
  transition: opacity 0.15s;
  touch-action: none;
}

.grip-space {
  width: 6px;
  flex-shrink: 0;
}

.warn {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: var(--gap);
}

.has-links {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: var(--text-3);
}

.status {
  flex-shrink: 0;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 1.5px dashed var(--text-3);

  &--exploratory {
    border-style: dotted;
    border-width: 2px;
  }
}

.label-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
}

.avatars {
  display: flex;
  flex-shrink: 0;
}

.avatar {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  margin-left: -5px;
  border: 2px solid var(--surface);
  border-radius: 50%;
  background: var(--iris-soft);
  color: var(--iris-strong);
  font-size: 9px;
  font-weight: 700;

  &--more {
    background: var(--bg);
    color: var(--text-2);
  }
}

.lane {
  grid-column: 2 / -1;
  z-index: 1;
  cursor: crosshair;
  border-radius: 8px;
  touch-action: none;
}

.block {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  min-width: 0;
  margin: 3px 2px;
  padding: 0 12px 0 15px;
  border: 1px solid color-mix(in srgb, var(--phase) 32%, var(--mix-base));
  border-radius: 8px;
  background: color-mix(in srgb, var(--phase) 13%, var(--mix-base));
  color: var(--phase-text);
  cursor: grab;
  touch-action: none;
  transition: border-color 0.15s, box-shadow 0.15s, opacity 0.2s;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 2px;
    background: var(--phase);
  }

  &:hover {
    border-color: var(--phase);

    .handle::after {
      opacity: 1;
    }
  }

  &.active {
    border-color: var(--phase);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--phase) 22%, transparent), 0 6px 16px -6px rgba(23, 20, 42, 0.25);
    z-index: 4;
  }

  &.dim {
    opacity: 0.18;
  }

  &--likely {
    border-style: dashed;
    border-color: color-mix(in srgb, var(--phase) 70%, var(--mix-base));
  }

  &--exploratory {
    border-style: dashed;
    border-color: color-mix(in srgb, var(--phase) 55%, var(--mix-base));
    background: repeating-linear-gradient(
      135deg,
      color-mix(in srgb, var(--phase) 13%, var(--mix-base)) 0 6px,
      color-mix(in srgb, var(--phase) 5%, var(--mix-base)) 6px 12px
    );
  }

  &.narrow {
    padding: 0 0 0 11px;

    .block-text {
      display: none;
    }
  }

  &:focus-visible {
    outline: 2px solid var(--phase);
    outline-offset: 1px;
  }
}

.block-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  pointer-events: none;
}

.handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 10px;
  z-index: 2;
  cursor: ew-resize;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 4px;
    height: 16px;
    margin-top: -8px;
    border-radius: 2px;
    background: var(--phase);
    opacity: 0;
    transition: opacity 0.15s;
  }

  &--left {
    left: -3px;

    &::after {
      left: 3px;
    }
  }

  &--right {
    right: -3px;

    &::after {
      right: 3px;
    }
  }
}

.ghost,
.hint {
  z-index: 3;
  margin: 3px 2px;
  border-radius: 8px;
  pointer-events: none;
}

.ghost {
  border: 1.5px dashed var(--iris);
  background: var(--iris-tint);
}

.hint {
  display: grid;
  place-items: center;
  border: 1px dashed var(--line-strong);
  color: var(--text-3);
}

.row-indicator {
  grid-column: 1 / -1;
  z-index: 6;
  align-self: start;
  height: 2px;
  margin-top: calc(var(--row-gap) / -2 - 1px);
  border-radius: 2px;
  background: var(--iris);
  pointer-events: none;
}

.quick-add {
  grid-column: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 10px;
  padding: 0 10px;
  border: 1px dashed var(--line-strong);
  border-radius: 8px;
  color: var(--text-3);
  transition: border-color 0.15s;

  &:focus-within {
    border-style: solid;
    border-color: var(--iris);
    background: var(--surface);
    color: var(--iris);
  }
}

.quick-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 13px;
  color: var(--text);

  &::placeholder {
    color: var(--text-3);
  }

  &:focus {
    outline: none;
  }
}

.empty-hint {
  grid-column: 2 / -1;
  align-self: center;
  padding-left: 16px;
  color: var(--text-3);
  font-size: 13px;
}
</style>

<style>
body.is-dragging,
body.is-dragging * {
  cursor: grabbing !important;
}

body.is-resizing,
body.is-resizing * {
  cursor: ew-resize !important;
}
</style>
